// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Card } from '@intakeform/ui';
import { MIN_PASSWORD_LENGTH, useChangePassword } from '../../api/auth.js';

/**
 * Self-service password change (intakeform-preview-r5t). Posts to
 * `/api/auth/change-password`; the API revokes other sessions on success.
 */
export function ChangePasswordCard() {
  const changePassword = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setLocalError(null);
    setDone(false);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setLocalError(`New password must be at least ${String(MIN_PASSWORD_LENGTH)} characters.`);
      return;
    }
    if (newPassword !== confirm) {
      setLocalError('New password and confirmation do not match.');
      return;
    }
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setDone(true);
          setCurrentPassword('');
          setNewPassword('');
          setConfirm('');
        },
      },
    );
  };

  const error =
    localError ??
    (changePassword.error ? changePassword.error.message || 'Could not change password.' : null);

  return (
    <Card heading="Change Password" variant="default">
      <p className="text-sm text-muted-foreground mb-4">
        Choose a new password of at least {MIN_PASSWORD_LENGTH} characters. Changing it signs out
        your other sessions.
      </p>
      <form onSubmit={onSubmit} aria-describedby={error ? 'change-password-error' : undefined}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
          <label htmlFor="current-password">
            <span>Current password</span>
            <input
              id="current-password"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              aria-required="true"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.currentTarget.value)}
              style={{ width: '100%' }}
            />
          </label>
          <label htmlFor="new-password">
            <span>New password</span>
            <input
              id="new-password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              required
              aria-required="true"
              minLength={MIN_PASSWORD_LENGTH}
              value={newPassword}
              onChange={(e) => setNewPassword(e.currentTarget.value)}
              style={{ width: '100%' }}
            />
          </label>
          <label htmlFor="confirm-password">
            <span>Confirm new password</span>
            <input
              id="confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              aria-required="true"
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              style={{ width: '100%' }}
            />
          </label>
          {error ? (
            <div
              id="change-password-error"
              className="error-banner"
              role="alert"
              aria-live="assertive"
            >
              {error}
            </div>
          ) : null}
          {done ? (
            <div
              role="status"
              aria-live="polite"
              className="text-sm font-medium"
              style={{ color: 'rgb(var(--color-primary))' }}
            >
              Password changed.
            </div>
          ) : null}
          <button
            type="submit"
            data-variant="primary"
            disabled={changePassword.isPending}
            style={{ alignSelf: 'flex-start' }}
          >
            {changePassword.isPending ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </Card>
  );
}
